/**
 * useUnifiedChangeTracker - оптимизированный хук для отслеживания изменений в таблице
 *
 * ОПТИМИЗАЦИЯ v2:
 * - Использует useRef вместо useState для хранения изменений (без React re-renders)
 * - Подсветка через cells callback (без hot.render())
 * - O(1) проверка изменений через Set
 *
 * v3:
 * - Убран sessionStorage-слой: он восстанавливал только метаданные изменений
 *   (без самих значений — данные приходят свежие из React Query), а у таблиц
 *   с resetOnBaselineChange сбрасывался сразу при маунте. Источник фантомных
 *   подсветок, а не персистентности.
 * - Ключи изменений сдвигаются при добавлении/удалении строк (shiftRowsFor*),
 *   baseline сплайсится синхронно — подсветка не «уезжает» на чужие строки.
 *
 * @see TrackedTable - рекомендуемый способ работы с таблицами
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CellChange,
  RowOperation,
  UnifiedChangeTrackerConfig,
  UnifiedChangeTracker
} from './types';
import { valuesEqual, createCellKey, parseCellKey } from './utils';

/**
 * Возвращает стабильный tracker-объект + changeVersion для trigger-а эффектов.
 * tracker мемоизирован и не пересоздаётся при changeCounter (setState) —
 * это предотвращает каскадный updateSettings() в HotTable при каждом редактировании ячейки.
 */
export interface UseUnifiedChangeTrackerResult {
  /** Стабильный tracker-объект (пересоздаётся только при смене config) */
  tracker: UnifiedChangeTracker;
  /** Монотонно растущий счётчик — используйте как dependency в useEffect/useCallback для реакции на изменения */
  changeVersion: number;
}

export function useUnifiedChangeTracker(config: UnifiedChangeTrackerConfig): UseUnifiedChangeTrackerResult {
  const baselineRef = useRef<Array<Array<string | number | boolean | null>> | null>(null);

  // ОПТИМИЗАЦИЯ: useRef вместо useState - без React re-renders при каждом изменении
  // changedSetRef - Set для быстрой O(1) проверки isChanged
  // changedDataRef - Map для хранения полных данных об изменениях
  const changedSetRef = useRef<Set<string>>(new Set());
  const changedDataRef = useRef<Map<string, CellChange>>(new Map());
  const operationsRef = useRef<Map<string, RowOperation>>(new Map());
  const derivedSetRef = useRef<Set<string>>(new Set());

  // Counter для триггера callbacks (onTrackingChange, dirty count)
  // Увеличивается только когда нужно уведомить внешний код
  const [changeCounter, setChangeCounter] = useState(0);

  // Снапшот baseline. Обновляется при каждой смене identity config.baseline
  // (рефетч после сохранения, WebSocket-инвалидация), но ТОЛЬКО пока у
  // пользователя нет несохранённых изменений — иначе сравнение шло бы
  // с эталоном, которого пользователь не видел.
  useEffect(() => {
    if (!config.baseline || !Array.isArray(config.baseline)) return;
    const hasPending = changedSetRef.current.size > 0 || operationsRef.current.size > 0;
    if (baselineRef.current && hasPending) return;
    baselineRef.current = config.baseline.map((row) => [...row]);
  }, [config.baseline]);

  // O(1) проверка - изменена ли ячейка пользователем
  const isChanged = useCallback((row: number, col: number): boolean => {
    const key = createCellKey(row, col);
    return changedSetRef.current.has(key);
  }, []);

  // O(1) проверка - является ли ячейка вычисляемой (derived)
  const isDerived = useCallback((row: number, col: number): boolean => {
    const key = createCellKey(row, col);
    return derivedSetRef.current.has(key) && !changedSetRef.current.has(key);
  }, []);

  // Регистрация изменения ячейки
  const registerChange = useCallback((row: number, col: number, newValue: unknown, originalValue?: unknown) => {
    const origin = originalValue !== undefined ? originalValue : baselineRef.current?.[row]?.[col];
    const key = createCellKey(row, col);
    const isEqual = valuesEqual(newValue, origin);

    if (isEqual) {
      // Значение вернулось к оригиналу - удаляем из изменённых
      if (changedSetRef.current.has(key)) {
        changedSetRef.current.delete(key);
        changedDataRef.current.delete(key);
        setChangeCounter(c => c + 1);
      }
      return;
    }

    // Добавляем/обновляем изменение
    changedSetRef.current.add(key);
    changedDataRef.current.set(key, {
      row,
      col,
      originalValue: origin,
      currentValue: newValue,
      timestamp: Date.now()
    });
    setChangeCounter(c => c + 1);
  }, []);

  const updateDerivedChanges = useCallback((newDerivedSet: Set<string>) => {
    // Проверяем изменилось ли что-то
    const current = derivedSetRef.current;
    if (current.size === newDerivedSet.size) {
      let isEqual = true;
      for (const key of current) {
        if (!newDerivedSet.has(key)) {
          isEqual = false;
          break;
        }
      }
      if (isEqual) return;
    }

    derivedSetRef.current = newDerivedSet;
    setChangeCounter(c => c + 1);
  }, []);

  const getDerivedChanges = useCallback((): Set<string> => {
    return derivedSetRef.current;
  }, []);

  // Регистрация операции со строкой (добавление/удаление)
  const registerRowOperation = useCallback((
    type: 'added' | 'deleted',
    visualIndex: number,
    rowId: string | number,
    rowData?: Array<string | number | boolean | null>
  ) => {
    if (type === 'deleted') {
      const deletedKey = `deleted:${rowId}`;
      const addedKey = `added:${rowId}`;

      // Если строка была добавлена и теперь удаляется - отменяем обе операции
      if (operationsRef.current.has(addedKey)) {
        operationsRef.current.delete(addedKey);
      } else {
        operationsRef.current.set(deletedKey, {
          type: 'deleted',
          visualIndex,
          rowId,
          rowData,
          timestamp: Date.now()
        });
      }
      setChangeCounter(c => c + 1);
    } else if (type === 'added') {
      const key = `added:${rowId}`;
      operationsRef.current.set(key, {
        type: 'added',
        visualIndex,
        rowId,
        rowData,
        timestamp: Date.now()
      });
      setChangeCounter(c => c + 1);
    }
  }, []);

  /**
   * Сдвиг row-части ключей изменений после удаления строк из источника данных.
   * Handsontable сплайсит исходный массив — физические индексы строк ниже
   * удалённой уменьшаются, и ключи `row:col` обязаны сдвинуться вместе с ними.
   * Изменения самих удалённых строк отбрасываются, baseline сплайсится синхронно.
   *
   * @param removedPhysicalRows - физические индексы удалённых строк (до удаления)
   */
  const shiftRowsForRemoval = useCallback((removedPhysicalRows: number[]) => {
    if (removedPhysicalRows.length === 0) return;
    // Обрабатываем сверху вниз (по убыванию), чтобы каждый сдвиг
    // не влиял на индексы ещё не обработанных удалений.
    const sorted = [...removedPhysicalRows].sort((a, b) => b - a);
    let mutated = false;

    for (const removed of sorted) {
      // Изменённые ячейки
      if (changedDataRef.current.size > 0) {
        const nextSet = new Set<string>();
        const nextData = new Map<string, CellChange>();
        changedDataRef.current.forEach((change, key) => {
          if (change.row === removed) {
            mutated = true;
            return;
          }
          if (change.row > removed) {
            const shifted = { ...change, row: change.row - 1 };
            const shiftedKey = createCellKey(shifted.row, shifted.col);
            nextSet.add(shiftedKey);
            nextData.set(shiftedKey, shifted);
            mutated = true;
          } else {
            nextSet.add(key);
            nextData.set(key, change);
          }
        });
        changedSetRef.current = nextSet;
        changedDataRef.current = nextData;
      }

      // Derived-ячейки (ключи того же формата `row:col`)
      if (derivedSetRef.current.size > 0) {
        const nextDerived = new Set<string>();
        derivedSetRef.current.forEach((key) => {
          const { row, field } = parseCellKey(key);
          const rowIndex = Number(row);
          if (rowIndex === removed) {
            mutated = true;
            return;
          }
          nextDerived.add(rowIndex > removed ? createCellKey(rowIndex - 1, field) : key);
          if (rowIndex > removed) mutated = true;
        });
        derivedSetRef.current = nextDerived;
      }

      // Baseline
      if (baselineRef.current && removed < baselineRef.current.length) {
        baselineRef.current.splice(removed, 1);
        mutated = true;
      }

      // Added-операции: их visualIndex указывает на физическую строку
      if (operationsRef.current.size > 0) {
        operationsRef.current.forEach((op, key) => {
          if (op.type === 'added' && op.visualIndex > removed) {
            operationsRef.current.set(key, { ...op, visualIndex: op.visualIndex - 1 });
            mutated = true;
          }
        });
      }
    }

    if (mutated) setChangeCounter(c => c + 1);
  }, []);

  /**
   * Сдвиг row-части ключей изменений после вставки строк в источник данных.
   * В baseline вставляются пустые строки-плейсхолдеры: у новых строк нет
   * эталона, и любое значение в них считается изменением.
   *
   * @param physicalIndex - физический индекс первой вставленной строки
   * @param amount - количество вставленных строк
   */
  const shiftRowsForInsertion = useCallback((physicalIndex: number, amount: number) => {
    if (amount <= 0) return;
    let mutated = false;

    if (changedDataRef.current.size > 0) {
      const nextSet = new Set<string>();
      const nextData = new Map<string, CellChange>();
      changedDataRef.current.forEach((change, key) => {
        if (change.row >= physicalIndex) {
          const shifted = { ...change, row: change.row + amount };
          const shiftedKey = createCellKey(shifted.row, shifted.col);
          nextSet.add(shiftedKey);
          nextData.set(shiftedKey, shifted);
          mutated = true;
        } else {
          nextSet.add(key);
          nextData.set(key, change);
        }
      });
      changedSetRef.current = nextSet;
      changedDataRef.current = nextData;
    }

    if (derivedSetRef.current.size > 0) {
      const nextDerived = new Set<string>();
      derivedSetRef.current.forEach((key) => {
        const { row, field } = parseCellKey(key);
        const rowIndex = Number(row);
        if (rowIndex >= physicalIndex) {
          nextDerived.add(createCellKey(rowIndex + amount, field));
          mutated = true;
        } else {
          nextDerived.add(key);
        }
      });
      derivedSetRef.current = nextDerived;
    }

    if (baselineRef.current && physicalIndex <= baselineRef.current.length) {
      baselineRef.current.splice(
        physicalIndex,
        0,
        ...Array.from({ length: amount }, () => [] as Array<string | number | boolean | null>),
      );
      mutated = true;
    }

    if (operationsRef.current.size > 0) {
      operationsRef.current.forEach((op, key) => {
        if (op.type === 'added' && op.visualIndex >= physicalIndex) {
          operationsRef.current.set(key, { ...op, visualIndex: op.visualIndex + amount });
          mutated = true;
        }
      });
    }

    if (mutated) setChangeCounter(c => c + 1);
  }, []);

  const getDeletedRows = useCallback((): RowOperation[] => {
    return Array.from(operationsRef.current.values()).filter((op) => op.type === 'deleted');
  }, []);

  const getAddedRows = useCallback((): RowOperation[] => {
    return Array.from(operationsRef.current.values()).filter((op) => op.type === 'added');
  }, []);

  const getAllOperations = useCallback((): RowOperation[] => {
    return Array.from(operationsRef.current.values());
  }, []);

  const getChangedCells = useCallback((): CellChange[] => {
    return Array.from(changedDataRef.current.values());
  }, []);

  const clearAllChanges = useCallback(() => {
    changedSetRef.current.clear();
    changedDataRef.current.clear();
    setChangeCounter(c => c + 1);
  }, []);

  const clearAllOperations = useCallback(() => {
    operationsRef.current.clear();
    setChangeCounter(c => c + 1);
  }, []);

  const reset = useCallback(() => {
    changedSetRef.current.clear();
    changedDataRef.current.clear();
    operationsRef.current.clear();
    derivedSetRef.current.clear();
    setChangeCounter(c => c + 1);
  }, []);

  const setBaseline = useCallback((baseline: Array<Array<string | number | boolean | null>>) => {
    baselineRef.current = baseline.map((row) => [...row]);
  }, []);

  const getBaseline = useCallback(() => {
    return baselineRef.current ? baselineRef.current.map((row) => [...row]) : null;
  }, []);

  // Геттер для hasChanges - читает из refs
  const getHasChanges = useCallback(() => {
    return changedSetRef.current.size > 0 ||
           derivedSetRef.current.size > 0 ||
           operationsRef.current.size > 0;
  }, []);

  const getChangesCount = useCallback(() => {
    return changedSetRef.current.size + derivedSetRef.current.size + operationsRef.current.size;
  }, []);

  // Мемоизируем tracker-объект, чтобы он оставался стабильной ссылкой между рендерами.
  // Все методы — стабильные useCallback ([]), поэтому tracker создаётся ровно один раз.
  // Без этой мемоизации tracker пересоздавался КАЖДЫЙ рендер → каскад:
  //   cellsCallback recreated → cells prop changed → settings recreated → HotTable.updateSettings() → visual jump
  const tracker = useMemo<UnifiedChangeTracker>(() => ({
    isChanged,
    isDerived,
    registerChange,
    updateDerivedChanges,
    getDerivedChanges,
    registerRowOperation,
    shiftRowsForRemoval,
    shiftRowsForInsertion,
    getDeletedRows,
    getAddedRows,
    get hasChanges() {
      return getHasChanges();
    },
    getChangesCount,
    getChangedCells,
    getAllOperations,
    reset,
    clearAllChanges,
    clearAllOperations,
    setBaseline,
    getBaseline
  }), [
    isChanged, isDerived, registerChange,
    updateDerivedChanges, getDerivedChanges,
    registerRowOperation, shiftRowsForRemoval, shiftRowsForInsertion,
    getDeletedRows, getAddedRows, getHasChanges,
    getChangesCount, getChangedCells, getAllOperations,
    reset, clearAllChanges, clearAllOperations, setBaseline, getBaseline
  ]);

  return { tracker, changeVersion: changeCounter };
}
